import { useSelector } from "react-redux";
import type { RootState } from "@store/store";
import { PERMISSIONS, type Permission, type UserRole } from "@constants/user.roles";

/**
 * Custom hook for handling role-based permissions
 * @description Provides utility functions to check user permissions based on their roles and permissions from API
 */
export const usePermissions = () => {
	const { user } = useSelector((state: RootState) => state.user);
	/**
	 * Get user's roles from the roles array
	 */
	const getUserRoles = (): string[] => {
		return user?.roles || [];
	};

	/**
	 * Get user's permissions from the permissions array
	 */
	const getUserPermissions = (): string[] => {
		return user?.permissions || [];
	};

	/**
	 * Check if user has a specific permission
	 * @param permission - The permission to check
	 * @returns boolean indicating if user has the permission
	 */
	const hasPermission = (permission: Permission): boolean => {
		const userPermissions = getUserPermissions();

		// Check if user has the specific permission
		if (userPermissions.includes(permission)) {
			return true;
		}

		// Check if user has "all.read" permission (super admin)
		if (userPermissions.includes(PERMISSIONS.ALL_READ)) {
			return true;
		}

		return false;
	};

	/**
	 * Check if user has any of the specified permissions
	 * @param permissions - Array of permissions to check
	 * @returns boolean indicating if user has any of the permissions
	 */
	const hasAnyPermission = (permissions: Permission[]): boolean => {
		const userPermissions = getUserPermissions();

		// Check if user has "all.read" permission (super admin)
		if (userPermissions.includes(PERMISSIONS.ALL_READ)) {
			return true;
		}

		return permissions.some(permission => userPermissions.includes(permission));
	};

	/**
	 * Check if user has all of the specified permissions
	 * @param permissions - Array of permissions to check
	 * @returns boolean indicating if user has all of the permissions
	 */
	const hasAllPermissions = (permissions: Permission[]): boolean => {
		const userPermissions = getUserPermissions();

		// Check if user has "all.read" permission (super admin)
		if (userPermissions.includes(PERMISSIONS.ALL_READ)) {
			return true;
		}

		return permissions.every(permission => userPermissions.includes(permission));
	};

	/**
	 * Check if user has a specific role
	 * @param role - The role to check
	 * @returns boolean indicating if user has the role
	 */
	const hasRole = (role: UserRole): boolean => {
		const userRoles = getUserRoles();
		return userRoles.includes(role);
	};

	/**
	 * Check if user has any of the specified roles
	 * @param roles - Array of roles to check
	 * @returns boolean indicating if user has any of the roles
	 */
	const hasAnyRole = (roles: UserRole[]): boolean => {
		const userRoles = getUserRoles();
		return roles.some(role => userRoles.includes(role));
	};

	/**
	 * Get user's primary role display name
	 */
	const getUserRoleDisplayName = (): string => {
		const userRoles = getUserRoles();
		if (userRoles.length === 0) return "Unknown Role";

		// Get the first role (primary role)
		const primaryRole = userRoles[0];

		const roleDisplayNames: Record<string, string> = {
			super_admin: "Super Admin",
			assistant_director: "Assistant Director",
			deputy_director: "Deputy Director",
			director: "Director",
			additional_director: "Additional Director",
			director_general: "Director General",
		};

		return roleDisplayNames[primaryRole] || primaryRole;
	};

	/**
	 * Get all user roles display names
	 */
	const getUserRolesDisplayNames = (): string[] => {
		const userRoles = getUserRoles();

		const roleDisplayNames: Record<string, string> = {
			super_admin: "Super Admin",
			assistant_director: "Assistant Director",
			deputy_director: "Deputy Director",
			director: "Director",
			additional_director: "Additional Director",
			director_general: "Director General",
		};

		return userRoles.map(role => roleDisplayNames[role] || role);
	};

	return {
		user,
		userRoles: getUserRoles(),
		userPermissions: getUserPermissions(),
		userRoleDisplayName: getUserRoleDisplayName(),
		userRolesDisplayNames: getUserRolesDisplayNames(),
		hasPermission,
		hasAnyPermission,
		hasAllPermissions,
		hasRole,
		hasAnyRole,
		PERMISSIONS, // Export permissions for easy access
	};
};
